import React, { useCallback, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Table, Row } from '@tanstack/react-table';
import styled from 'styled-components';
import { TableSection, EmptyState } from '../PersonalBusinessReviewView.styles';
import { DataEntryRowDataType } from '../types';

const shimmerAnimation = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const VirtualizedContainer = styled.div`
  width: 100%;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  height: 100%;

  ${shimmerAnimation}
`;

const VirtualizedHeader = styled.div`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e1e5e9;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
`;

const VirtualizedHeaderRow = styled.div`
  display: table-row;
`;

const VirtualizedHeaderCell = styled.div<{
  align?: 'left' | 'right';
  width: number;
  canSort?: boolean;
}>`
  display: table-cell;
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  width: ${props => props.width}px;
  font-weight: 600;
  color: #374151;
  cursor: ${props => (props.canSort ? 'pointer' : 'default')};
  vertical-align: middle;
  white-space: nowrap;

  &:hover {
    ${props => props.canSort && 'background-color: #e5e7eb;'}
  }
`;

const VirtualizedBody = styled.div`
  width: 100%;
  position: relative;
  flex: 1;
  overflow: hidden;
`;

interface VirtualizedPersonalBusinessReviewTableProps {
  table: Table<DataEntryRowDataType>;
  rowData: DataEntryRowDataType[];
  pendingClassifications: Set<string>;
  onRowClick: (row: DataEntryRowDataType) => void;
  searchTerm?: string | null;
  height?: number;
}

const VirtualizedRowContainer = styled.div<{ isSelected?: boolean; isPending?: boolean }>`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  cursor: pointer;
  border-bottom: 1px solid #e1e5e9;

  ${props => {
    if (props.isPending && props.isSelected) {
      return `
        background: linear-gradient(90deg, #e3f2fd 0%, #e3f2fd 40%, rgba(255, 179, 0, 0.2) 50%, #e3f2fd 60%, #e3f2fd 100%);
        background-size: 200% 100%;
        animation: shimmer 2s infinite linear;
      `;
    }
    if (props.isSelected) {
      return 'background-color: #e3f2fd;';
    }
    if (props.isPending) {
      return `
        background: linear-gradient(90deg, #f5f5f5 0%, #f5f5f5 40%, rgba(255, 179, 0, 0.3) 50%, #f5f5f5 60%, #f5f5f5 100%);
        background-size: 200% 100%;
        animation: shimmer 2s infinite linear;
      `;
    }
  }}

  &:hover {
    background-color: #f8f9fa;
  }
`;

const VirtualizedCell = styled.div<{ align?: 'left' | 'right'; width: number }>`
  display: table-cell;
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  width: ${props => props.width}px;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    rows: Row<DataEntryRowDataType>[];
    pendingClassifications: Set<string>;
    onRowClick: (row: DataEntryRowDataType) => void;
    columnWidths: number[];
  };
}

const VirtualizedRow: React.FC<RowProps> = ({ index, style, data }) => {
  const { rows, pendingClassifications, onRowClick, columnWidths } = data;
  const row = rows[index];

  const handleRowClick = useCallback(() => {
    onRowClick(row.original as DataEntryRowDataType);
  }, [onRowClick, row.original]);

  return (
    <div style={style}>
      <VirtualizedRowContainer
        onClick={handleRowClick}
        isSelected={row.getIsSelected()}
        isPending={pendingClassifications.has(row.original.id)}
      >
        {row.getVisibleCells().map((cell, cellIndex) => (
          <VirtualizedCell
            key={cell.id}
            align={cell.column.id === 'amount' ? 'right' : 'left'}
            width={columnWidths[cellIndex]}
          >
            {typeof cell.column.columnDef.cell === 'function'
              ? cell.column.columnDef.cell(cell.getContext())
              : cell.getValue()}
          </VirtualizedCell>
        ))}
      </VirtualizedRowContainer>
    </div>
  );
};

export const VirtualizedPersonalBusinessReviewTable: React.FC<
  VirtualizedPersonalBusinessReviewTableProps
> = ({ table, rowData, pendingClassifications, onRowClick, searchTerm, height = 400 }) => {
  const listRef = useRef<List>(null);
  const scrollTopRef = useRef<number>(0);

  const emptyStateMessage = searchTerm
    ? `No transactions found matching "${searchTerm}"`
    : 'No unclassified transactions found';

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  const columnWidths =
    headerGroups.length === 0 ? [] : headerGroups[0].headers.map(header => header.getSize());

  const itemData = {
    rows,
    pendingClassifications,
    onRowClick,
    columnWidths,
  };

  // Preserve scroll position when component re-renders
  useEffect(() => {
    if (listRef.current && scrollTopRef.current > 0) {
      listRef.current.scrollTo(scrollTopRef.current);
    }
  });

  const handleScroll = useCallback(({ scrollTop }: { scrollTop: number }) => {
    scrollTopRef.current = scrollTop;
  }, []);

  if (rowData.length === 0) {
    return (
      <TableSection>
        <EmptyState>{emptyStateMessage}</EmptyState>
      </TableSection>
    );
  }

  return (
    <TableSection>
      <VirtualizedContainer>
        <VirtualizedHeader>
          {headerGroups.map(headerGroup => (
            <VirtualizedHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <VirtualizedHeaderCell
                  key={header.id}
                  width={header.getSize()}
                  canSort={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  align={header.id === 'amount' ? 'right' : 'left'}
                >
                  {header.isPlaceholder
                    ? null
                    : typeof header.column.columnDef.header === 'function'
                    ? header.column.columnDef.header(header.getContext())
                    : header.column.columnDef.header}
                </VirtualizedHeaderCell>
              ))}
            </VirtualizedHeaderRow>
          ))}
        </VirtualizedHeader>

        <VirtualizedBody>
          <List
            ref={listRef}
            height={height}
            itemCount={rows.length}
            itemSize={49} // Row height (48px) + border (1px)
            itemData={itemData}
            width="100%"
            onScroll={handleScroll}
          >
            {VirtualizedRow}
          </List>
        </VirtualizedBody>
      </VirtualizedContainer>
    </TableSection>
  );
};
